import profile from "@/profiles/profileSingleton"
import { isCenterAll } from "@/lib/utils"
import {
  entryHasTrimbleDeveloperNewsCenterTag,
  mapTrimbleDeveloperNewsContentfulSlugToUrl,
  mapTrimbleDeveloperNewsUrlSlugToContentful,
  TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG,
  TRIMBLE_DEVELOPER_NEWS_URL_PREFIX,
  usesTrimbleDeveloperNewsUrls,
} from "@/lib/url/trimbleDeveloperNews"

/**
 * Maps Contentful center metadata tags to the center slug used in
 * user-facing URLs.
 *
 * The keys are the same tag IDs used in `rcTagNames` on siteConfig and
 * in the `contentfulMetadata.tags` array on every RC entry.
 */
export const CENTER_TAG_TO_SLUG: Record<string, string> = {
  centerCorporate: "trimble",
  centerCivil: "construction",
  // O&PS content is surfaced under the same /blog/construction and
  // /resources/construction paths as civil on corporate.
  centerOps: "construction",
  centerDevelopment: "development",
  centerOemgnss: "oemgnss",
  centerApplanix: "applanix",
  centerTransportation: "transportation",
  centerPositioning: "positioning",
  centerSketchup: "sketchup",
  centerGeospatial: "geospatial",
  centerTdNews: TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG,
}

/**
 * URL base path per content-type family.
 *  - Articles live under `/blog/{center}/article/{slug}`
 *  - Everything else lives under `/resources/{center}/{type}/{slug}`
 */
const ARTICLE_TYPES = new Set(["article", "Article"])

export function getUrlBaseForType(type?: string): "blog" | "resources" {
  if (type && ARTICLE_TYPES.has(type)) return "blog"
  return "resources"
}

// ---------------------------------------------------------------------------
// Single-center site support
// ---------------------------------------------------------------------------

/**
 * Sites that only ever host a single center's RC content. On these sites,
 * we omit the center-slug segment from user-facing URLs, so URLs look like
 * `/blog/article/{slug}` instead of `/blog/sketchup/article/{slug}`.
 *
 * Keys are `NEXT_PUBLIC_SITE_MACHINE_NAME`. Values are the Contentful
 * center tag ID the site is dedicated to.
 */
const SINGLE_CENTER_SITES: Record<string, string> = {
  sketchup: "centerSketchup",
}

/**
 * Center tag ID for the current site if it is a single-center site.
 * Returns `undefined` for multi-center sites (corporate, geospatial, etc.).
 */
export function getSingleCenterTag(): string | undefined {
  const site = profile.NEXT_PUBLIC_SITE_MACHINE_NAME as string | undefined
  return site ? SINGLE_CENTER_SITES[site] : undefined
}

/** URL center slug (e.g. `"sketchup"`) for the current single-center site. */
export function getSingleCenterSlug(): string | undefined {
  const tag = getSingleCenterTag()
  return tag ? CENTER_TAG_TO_SLUG[tag] : undefined
}

/** True when the current site should emit center-less RC URLs. */
export function isSingleCenterSite(): boolean {
  return getSingleCenterTag() !== undefined
}

/**
 * Map a user-facing landing-page URL slug to the Contentful slug.
 * On single-center sites, `/blog` → `blog/{center}` and
 * `/resources` → `resources/{center}`.
 */
export function toContentfulSlug(urlSlug: string): string {
  const tdNewsSlug = mapTrimbleDeveloperNewsUrlSlugToContentful(urlSlug)
  if (tdNewsSlug !== urlSlug) return tdNewsSlug

  const singleCenter = getSingleCenterSlug()
  if (!singleCenter) return urlSlug
  if (urlSlug === "blog") return `blog/${singleCenter}`
  if (urlSlug === "resources") return `resources/${singleCenter}`
  return urlSlug
}

/**
 * Map a Contentful RC landing-page slug to the user-facing URL slug.
 * Inverse of `toContentfulSlug`.
 */
export function toUserFacingSlug(contentfulSlug: string): string {
  const tdNewsSlug = mapTrimbleDeveloperNewsContentfulSlugToUrl(contentfulSlug)
  if (tdNewsSlug !== contentfulSlug) return tdNewsSlug

  const singleCenter = getSingleCenterSlug()
  if (!singleCenter) return contentfulSlug
  if (contentfulSlug === `blog/${singleCenter}`) return "blog"
  if (contentfulSlug === `resources/${singleCenter}`) return "resources"
  return contentfulSlug
}

/** @deprecated Use CENTER_TAG_TO_SLUG + getUrlBaseForType instead */
export const CENTER_TAG_TO_BLOG_PREFIX: Record<string, string> =
  Object.fromEntries(
    Object.entries(CENTER_TAG_TO_SLUG).map(([tag, slug]) => [
      tag,
      `blog/${slug}`,
    ]),
  )

/**
 * The "all" slug is a wildcard prefix used by centerAll sites (e.g. preview)
 * to render RC content from any center under a single path:
 *   /blog/all/article/...
 *   /resources/all/case-study/...
 */
export const BLOG_SLUG_ALL = "all"

/**
 * Reverse lookup: center slug (e.g. "trimble") → set of center tags that
 * are allowed to render under that slug.
 *
 * Built automatically from CENTER_TAG_TO_SLUG.
 */
export const BLOG_SLUG_TO_CENTER_TAGS: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {}
  for (const [tag, slug] of Object.entries(CENTER_TAG_TO_SLUG)) {
    if (!map[slug]) map[slug] = []
    map[slug].push(tag)
  }
  return map
})()

/**
 * Structured view of every content prefix that this site's URLs can use.
 *
 * - Multi-center sites emit one entry per (base, center) pair — e.g.
 *   `{ base: "blog", centerSlug: "trimble", urlPrefix: "blog/trimble" }`.
 * - Single-center sites emit one entry per base — the center is folded
 *   into the internal `_bp-` rewrite but is *not* part of the URL:
 *   `{ base: "blog", centerSlug: "sketchup", urlPrefix: "blog" }`.
 */
export interface ContentPrefixInfo {
  base: "blog" | "resources"
  /** The center slug used internally for `_bp-{slug}` and validation. */
  centerSlug: string
  /** The user-facing URL prefix (may omit the center on single-center sites). */
  urlPrefix: string
}

export function getAllContentPrefixInfos(): ContentPrefixInfo[] {
  const tdNewsPrefixes: ContentPrefixInfo[] = usesTrimbleDeveloperNewsUrls()
    ? [
        {
          base: "blog",
          centerSlug: TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG,
          urlPrefix: TRIMBLE_DEVELOPER_NEWS_URL_PREFIX,
        },
      ]
    : []

  const bases: ContentPrefixInfo["base"][] = ["blog", "resources"]
  const singleCenter = getSingleCenterSlug()
  if (singleCenter) {
    return [
      ...tdNewsPrefixes,
      ...bases.map(base => ({
        base,
        centerSlug: singleCenter,
        urlPrefix: base,
      })),
    ]
  }
  const centerSlugs = [
    ...new Set(Object.values(CENTER_TAG_TO_SLUG)),
    BLOG_SLUG_ALL,
  ]
  return [
    ...tdNewsPrefixes,
    ...bases.flatMap(base =>
      centerSlugs.map(centerSlug => ({
        base,
        centerSlug,
        urlPrefix: `${base}/${centerSlug}`,
      })),
    ),
  ]
}

/**
 * All known user-facing content prefixes across both families (blog +
 * resources). Used by revalidation and RC landing-page prefix detection.
 *
 * Order matters for `startsWith` matching in `renderPage.tsx`: longer
 * prefixes come first so `"blog/trimble"` wins over `"blog"` where both
 * would match.
 */
export const ALL_CONTENT_PREFIXES: string[] = getAllContentPrefixInfos()
  .map(info => info.urlPrefix)
  .sort((a, b) => b.length - a.length)

/** @deprecated Use ALL_CONTENT_PREFIXES instead */
export const ALL_BLOG_PREFIXES: string[] = ALL_CONTENT_PREFIXES

// ---------------------------------------------------------------------------
// Entry-level helpers
// ---------------------------------------------------------------------------

/**
 * Return the center slug for an RC entry by inspecting its
 * `contentfulMetadata.tags` array.
 *
 * Returns `undefined` when no mapping exists.
 */
export function getCenterSlugForEntry(entry: any): string | undefined {
  const tags: { id?: string }[] = entry?.contentfulMetadata?.tags ?? []
  for (const tag of tags) {
    const slug = CENTER_TAG_TO_SLUG[tag.id ?? ""]
    if (slug) return slug
  }
  return undefined
}

/**
 * Return the full URL prefix (e.g. "resources/trimble" or "blog/trimble")
 * for an RC entry, choosing the base path based on content type.
 *
 * On single-center sites (e.g. sketchup) the center segment is omitted so
 * URLs are `/blog/...` and `/resources/...` instead of `/blog/sketchup/...`.
 */
export function getContentPrefixForEntry(
  entry: any,
  type?: string,
): string | undefined {
  if (
    usesTrimbleDeveloperNewsUrls() &&
    entryHasTrimbleDeveloperNewsCenterTag(entry)
  ) {
    return TRIMBLE_DEVELOPER_NEWS_URL_PREFIX
  }

  const base = getUrlBaseForType(type)
  if (isSingleCenterSite()) {
    // Only accept entries whose center matches this single-center site.
    const centerSlug = getCenterSlugForEntry(entry)
    if (!centerSlug || centerSlug === getSingleCenterSlug()) {
      return base
    }
    // Cross-center content on a single-center site is unusual, but if it
    // happens we still fall back to the bare base so the URL is well-formed.
    return base
  }
  const centerSlug = getCenterSlugForEntry(entry)
  if (!centerSlug) return undefined
  return `${base}/${centerSlug}`
}

/**
 * Return the full URL prefix for an entry, falling back to the site default.
 * The base path ("blog" or "resources") is chosen by content type.
 */
export function getContentPrefixOrDefault(entry?: any, type?: string): string {
  const base = getUrlBaseForType(type)
  if (isSingleCenterSite()) {
    return base
  }
  if (entry) {
    const prefix = getContentPrefixForEntry(entry, type)
    if (prefix) return prefix
  }
  return `${base}/trimble`
}

/** @deprecated Use getContentPrefixOrDefault instead */
export function getBlogPrefixOrDefault(entry?: any): string {
  return getContentPrefixOrDefault(entry, "article")
}

/** @deprecated Use getContentPrefixForEntry instead */
export function getBlogPrefixForEntry(entry: any): string | undefined {
  return getContentPrefixForEntry(entry, "article")
}

// ---------------------------------------------------------------------------
// Slug extraction (used by rewrites that encode the blog slug in the path)
// ---------------------------------------------------------------------------

const BLOG_SLUG_PREFIX = "_bp-"
export const ENTRY_TYPE_ARTICLES = "_et-articles"
export const ENTRY_TYPE_OTHER = "_et-other"

export type RcEntriesType = "articles" | "other"

/**
 * Parse the blog slug and entry type out of a tags/collection route's
 * `[...slug]` array.
 *
 * Rewrites encode center as `_bp-{center}` and entry type as
 * `_et-articles` (blog) or `_et-other` (resources) before the tag slug(s).
 */
export function extractTagsRouteContext(slugArray: string[]): {
  blogSlug: string | undefined
  entriesType: RcEntriesType | undefined
  contentSlug: string[]
} {
  const { blogSlug, contentSlug } = extractBlogContext(slugArray)
  if (!blogSlug || contentSlug.length === 0) {
    return { blogSlug, entriesType: undefined, contentSlug }
  }

  const [entryTypeSegment, ...rest] = contentSlug
  if (entryTypeSegment === ENTRY_TYPE_ARTICLES) {
    return { blogSlug, entriesType: "articles", contentSlug: rest }
  }
  if (entryTypeSegment === ENTRY_TYPE_OTHER) {
    return { blogSlug, entriesType: "other", contentSlug: rest }
  }

  return { blogSlug, entriesType: undefined, contentSlug }
}

/**
 * Build `[...slug]` tuples for tags/collection static params, emitting both
 * blog (`_et-articles`) and resources (`_et-other`) entry types per center.
 */
export function buildStaticParamSlugsForTagsRoute(
  tagSlugParts: string[],
  allowableCenters: string[] = [],
): string[][] {
  const centerSlugs = new Set<string>()
  const singleCenter = getSingleCenterSlug()
  if (singleCenter) {
    centerSlugs.add(singleCenter)
  } else if (isCenterAll(allowableCenters)) {
    centerSlugs.add(BLOG_SLUG_ALL)
  } else {
    for (const tagId of allowableCenters) {
      const centerSlug = CENTER_TAG_TO_SLUG[tagId]
      if (centerSlug) centerSlugs.add(centerSlug)
    }
  }

  const results: string[][] = []
  for (const centerSlug of centerSlugs) {
    const bp = `${BLOG_SLUG_PREFIX}${centerSlug}`
    results.push([bp, ENTRY_TYPE_ARTICLES, ...tagSlugParts])
    results.push([bp, ENTRY_TYPE_OTHER, ...tagSlugParts])
  }
  return results
}

/**
 * Parse the blog slug out of a route's `[...slug]` array.
 *
 * Next.js rewrites encode the blog slug as the *first* slug segment with a
 * `_bp-` prefix (e.g. `['_bp-trimble', 'my-article']`). Every valid RC URL
 * — including on single-center sites like sketchup — reaches these routes
 * through a rewrite from `/blog/...` or `/resources/...` that injects the
 * prefix (see `next.config.ts`). Requests that hit the internal flat shape
 * directly (e.g. `/en/tags/thought-leadership`) have no `_bp-` segment; we
 * return `blogSlug: undefined` so page handlers can 404 them.
 */
export function extractBlogContext(slugArray: string[]): {
  blogSlug: string | undefined
  contentSlug: string[]
} {
  if (slugArray.length > 0 && slugArray[0].startsWith(BLOG_SLUG_PREFIX)) {
    return {
      blogSlug: slugArray[0].slice(BLOG_SLUG_PREFIX.length),
      contentSlug: slugArray.slice(1),
    }
  }
  return { blogSlug: undefined, contentSlug: slugArray }
}

// ---------------------------------------------------------------------------
// Static param generation
// ---------------------------------------------------------------------------

/**
 * Build the `[...slug]` tuples that `generateStaticParams` should pre-render
 * for a given RC entry.
 *
 * All RC URLs reach their route handler through a rewrite that encodes the
 * center as a `_bp-{centerSlug}` prefix in the slug array (see
 * `next.config.ts`). Pre-rendering slugs without that prefix produces cache
 * keys that never match a real request, forcing every hit through cold
 * on-demand SSR. This helper injects the correct prefix per applicable center.
 *
 * Rules:
 *  - Single-center sites (e.g. sketchup): always emit the site's single
 *    center slug regardless of the entry's tags.
 *  - `centerAll` deployments (e.g. preview): additionally emit an entry under
 *    the `_bp-all` wildcard.
 *  - Multi-center sites: emit one entry per center tag the entry carries
 *    that is also present in `allowableCenters` (the site's `rcTagNames`).
 *
 * The returned arrays are the `[...slug]` value only — the caller is
 * responsible for pairing them with `lang` on the returned param object.
 */
export function buildStaticParamSlugsForEntry(
  entry: {
    slug?: string
    contentfulMetadata?: { tags?: { id?: string | null }[] | null } | null
  },
  allowableCenters: string[] = [],
): string[][] {
  if (!entry?.slug) return []
  const slugParts = entry.slug.split("/")
  const results: string[][] = []
  const seen = new Set<string>()

  const push = (centerSlug: string) => {
    const key = `${BLOG_SLUG_PREFIX}${centerSlug}`
    if (seen.has(key)) return
    seen.add(key)
    results.push([key, ...slugParts])
  }

  const singleCenter = getSingleCenterSlug()
  if (singleCenter) {
    push(singleCenter)
    return results
  }

  const isAll = isCenterAll(allowableCenters)
  if (isAll) {
    push(BLOG_SLUG_ALL)
  }

  const entryTags = entry?.contentfulMetadata?.tags ?? []
  for (const tag of entryTags) {
    const tagId = tag?.id
    if (!tagId) continue
    if (!isAll && allowableCenters.length > 0 && !allowableCenters.includes(tagId)) {
      continue
    }
    const centerSlug = CENTER_TAG_TO_SLUG[tagId]
    if (!centerSlug) continue
    push(centerSlug)
  }
  return results
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Check whether a given blog slug (e.g. `"trimble"`, `"development"`) is
 * valid for an RC entry based on the entry's `contentfulMetadata.tags`.
 *
 * The special slug `"all"` is always valid — it serves centerAll sites that
 * render content from every center under `/blog/all/`.
 *
 * Returns `true` if at least one of the entry's center tags maps to the
 * supplied blog slug.
 */
export function validateBlogSlugForEntry(
  blogSlug: string,
  entry: any,
): boolean {
  if (blogSlug === BLOG_SLUG_ALL) return true

  const allowedCenters = BLOG_SLUG_TO_CENTER_TAGS[blogSlug]
  if (!allowedCenters || allowedCenters.length === 0) return false

  const entryTags: string[] = (entry?.contentfulMetadata?.tags ?? [])
    .map((t: any) => t?.id)
    .filter(Boolean)

  return allowedCenters.some(center => entryTags.includes(center))
}
